import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/indices.png';

import { Zap, Clock, Layers, RefreshCw, PieChart, Wrench } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/indices');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/index-leadership-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/index-concentration-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/index-specs');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/pooled-vehicle-comparison-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);



const StocksPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Stock Index API – Global Market Indices Data"
        description="Stock index API with daily and historical data for 50,000+ global market indices, including constituents, weights, and rebalancing. Start for free."
        canonicalUrl="https://axionquant.com/products/indices"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Stock Index API",
          "description": "Stock index API with daily and historical data for 50,000+ global market indices, including constituents, weights, and rebalancing.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Indices Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
      docs="/docs/01-market-data/indices"
      title="Stock Index API: Deconstruct the Benchmark"
      body="Go beyond the index value. Analyze daily and historical market index data, constituent weights, sector rotations, and performance attribution for 50,000+ global indices and benchmarks."
      crumb={{name: 'Indices', path:'/products/indices'}}
      tabs={
      [
        {
          label: 'Weight Drift',
          language: 'python',
          filename: 'weight_drift.py',
          bg: '4.png',
          code: `from axion import Axion, utils

client = Axion(api_key="your_api_key")

etf = "QQQ"

# Snapshot today's constituent weights, cached to disk
today = utils.df(utils.cache(
    f"{etf}_holdings_{utils.d(utils.today)}",
    lambda: client.etfs.holdings(etf)
))

# Yesterday's snapshot for comparison
prior = utils.df(utils.read(f"{etf}_holdings_{utils.d(utils.yesterday)}"))

# Align side by side and measure the change
merged = utils.snap(
    [today, prior],
    names=["now", "prior"],
    overwrite=["holdingPercent"],
    col="symbol"
)

merged["weight_change"] = merged["now"] - merged["prior_holdingPercent"]

merged.sort_values("weight_change", ascending=False).head(5)`,
        },
        {
          label: 'Sector Exposure',
          language: 'python',
          filename: 'sector_exposure.py',
          bg: '2.png',
          code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

# Constituent weights for the benchmark proxy
holdings = utils.df(client.etfs.holdings("SPY"))

# Tag each constituent with its sector
sectors = {}

def fetch_sector(row):
    profile = client.profiles.profile(row["symbol"])
    return (row["symbol"], profile["sector"])

utils.work(holdings, fetch_sector, sectors)

holdings["sector"] = holdings["symbol"].map(sectors)

# Roll holdings up into sector-level exposure
exposure = holdings.groupby("sector")["holdingPercent"].sum().reset_index()

visualize.pie(exposure, values="holdingPercent", labels="sector")`,
        },
        {
          label: 'Sector Rotation',
          language: 'python',
          filename: 'sector_rotation.py',
          bg: '3.png',
          code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

# SPDR sector ETFs spanning the S&P 500 sectors
sector_etfs = utils.df([{"symbol": s} for s in [
    "XLK", "XLF", "XLE", "XLV", "XLY",
    "XLP", "XLI", "XLB", "XLU", "XLRE", "XLC"
]])

prices = {}

def fetch_prices(row):
    data = client.stocks.prices(row["symbol"], from_date=utils.d(utils.monthago))
    return (row["symbol"], utils.df(data))

utils.work(sector_etfs, fetch_prices, prices)

# Rank sectors by one-month momentum
rotation = utils.gainers(prices, frame=20, limit=11)

visualize.barh(rotation, x="change", y="ticker")`,
        },
        {
          label: 'Attribution',
          language: 'python',
          filename: 'attribution.py',
          bg: '1.png',
          code: `from axion import Axion, utils, models

client = Axion(api_key="your_api_key")

constituent = "NVDA"
benchmark = "SPY"

# One year of daily closes for both series
stock = utils.df(client.stocks.prices(constituent, from_date=utils.d(utils.yearago)))
bench = utils.df(client.stocks.prices(benchmark, from_date=utils.d(utils.yearago)))

stock = stock.rename(columns={"close": "stock_close"})
bench = bench.rename(columns={"close": "bench_close"})

merged = utils.stitch([stock, bench], col="time")
merged = utils.relativity(merged, cols=["stock_close", "bench_close"])

# Sensitivity of the constituent to the benchmark
beta = models.beta(merged, x="relative_stock_close", y="relative_bench_close")

# Split total return into benchmark-driven and stock-specific components
bench_return = merged["relative_bench_close"].sum()
stock_return = merged["relative_stock_close"].sum()

systematic = beta * bench_return
specific = stock_return - systematic`,
        }]
    } />

      <Marquee/>

      <FeaturesGrid
        title="Stock Index API & Intelligence. "
        subtitle="Global market indices, historical data, and analytics. "
        features={[
          {
            fig: 'FIG 0.1',
            icon: <Zap size={80} color="var(--purple)" />,
            title: 'Daily Levels & Deep History',
            description:
            'Daily index values, daily changes, and full historical stock index data for 50,000+ global benchmarks. Access decades of daily and hourly history via a simple REST API.',
          },
          {
            fig: 'FIG 0.2',
            icon: <Clock size={80} color="var(--green)" />,
            title: 'Historical Data',
            description:
            '40+ years of daily index price history. Full backtesting of index-linked strategies with accurate corporate action adjustments.',
          },
          {
            fig: 'FIG 0.3',
            icon: <Layers size={80} color="var(--red)" />,
            title: 'Constituents & Weights',
            description:
            'Complete constituent lists with market cap, weightings, and inclusion dates. Drill into sector breakdowns and security-level index composition data.',
          },
          {
            fig: 'FIG 0.4',
            icon: <RefreshCw size={80} color="var(--purple)" />,
            title: 'Rebalancing Calendar',
            description:
            'Scheduled rebalancing dates, methodology changes, and index reconstitution events. Stay ahead of index-driven flows and sector rotations.',
          },
          {
            fig: 'FIG 0.5',
            icon: <PieChart size={80} color="var(--green)" />,
            title: 'Performance Attribution',
            description:
            'Decompose index returns by sector, country, factor, and individual stock contribution. Identify the true drivers of benchmark outperformance.',
          },
          {
            fig: 'FIG 0.6',
            icon: <Wrench size={80} color="var(--red)" />,
            title: 'Custom Index Creation',
            description:
            'Build, backtest, and monitor custom baskets or thematic indices with the same rigor as major index providers. Model bespoke benchmarks across the global indices universe.',
          },
        ]}
      />


      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Indices Data Platform"
        headingPlain="Index data"
        headingEm="across 50,000+ benchmarks."
        body="Daily and hourly market index data, full constituent and weight history, and performance attribution for 50,000+ global benchmarks. Optimized for long-horizon research across equities, fixed income, commodities, and custom sectors."
        stats={[
          { v: "50,000+", l: "Indices Tracked" },
          { v: "8+",      l: "Asset Classes"  },
          { v: "40+yr",   l: "History"        },
          { v: "100%",    l: "Daily Updates"  },
        ]}
        markers={[
          { id: "us-indices", location: [40.71, -74.01], label: "S&P 500 & Nasdaq-100" },
          { id: "dow-jones", location: [40.71, -74.01], label: "Dow Jones Industrial Average" },
          { id: "russell-2000", location: [40.71, -74.01], label: "Russell 2000" },

          { id: "ftse-100", location: [51.51, -0.09], label: "FTSE 100" },
          { id: "ftse-250", location: [51.51, -0.09], label: "FTSE 250" },
          { id: "dax", location: [50.11, 8.68], label: "DAX 40" },
          { id: "cac-40", location: [48.86, 2.35], label: "CAC 40" },
          { id: "euro-stoxx-50", location: [50.11, 8.68], label: "Euro Stoxx 50" },
          { id: "ibex-35", location: [40.42, -3.70], label: "IBEX 35" },
          { id: "ftse-mib", location: [45.46, 9.19], label: "FTSE MIB" },
          { id: "aex", location: [52.37, 4.90], label: "AEX Amsterdam" },
          { id: "bel-20", location: [50.85, 4.35], label: "BEL 20" },
          { id: "smi", location: [47.37, 8.54], label: "Swiss Market Index" },
          { id: "omx-stockholm", location: [59.33, 18.07], label: "OMX Stockholm 30" },
          { id: "oslo-obbx", location: [59.91, 10.75], label: "OBX Oslo" },
          { id: "micex-russia", location: [55.75, 37.62], label: "MOEX Russia" },

          { id: "nikkei-225", location: [35.68, 139.77], label: "Nikkei 225" },
          { id: "topix", location: [35.68, 139.77], label: "TOPIX" },
          { id: "hang-seng", location: [22.28, 114.16], label: "Hang Seng Index" },
          { id: "shanghai-composite", location: [31.23, 121.47], label: "Shanghai Composite" },
          { id: "szse-component", location: [22.54, 114.06], label: "Shenzhen Component" },
          { id: "csi-300", location: [31.23, 121.47], label: "CSI 300" },
          { id: "kospi", location: [37.57, 126.98], label: "KOSPI South Korea" },
          { id: "straits-times", location: [1.28, 103.85], label: "Straits Times Index" },
          { id: "nifty-50", location: [19.07, 72.88], label: "NIFTY 50 India" },
          { id: "sensex", location: [19.07, 72.88], label: "BSE Sensex" },
          { id: "taiex", location: [25.03, 121.56], label: "Taiwan Weighted Index" },
          { id: "jakarta-composite", location: [-6.20, 106.85], label: "Jakarta Composite Index" },
          { id: "set-index", location: [13.76, 100.50], label: "SET Thailand" },
          { id: "vietnam-ho-chi-minh", location: [10.82, 106.63], label: "VN-Index Vietnam" },
          { id: "klse", location: [3.14, 101.69], label: "FTSE Bursa Malaysia KLCI" },
          { id: "nzx-50", location: [-36.85, 174.76], label: "NZX 50 New Zealand" },

          { id: "sp-asx-200", location: [-33.87, 151.21], label: "S&P/ASX 200" },
          { id: "all-ordinaries", location: [-33.87, 151.21], label: "All Ordinaries Australia" },

          { id: "tsx-composite", location: [43.65, -79.38], label: "S&P/TSX Composite Canada" },
          { id: "tsx-60", location: [43.65, -79.38], label: "S&P/TSX 60 Canada" },
          { id: "bovespa", location: [-23.55, -46.63], label: "Bovespa Brazil" },
          { id: "ipc-mexico", location: [19.43, -99.13], label: "S&P/BMV IPC Mexico" },
          { id: "merval", location: [-34.60, -58.38], label: "MERVAL Argentina" },
          { id: "ipsa-chile", location: [-33.45, -70.66], label: "IPSA Chile" },

          { id: "jse-top40", location: [-26.20, 28.04], label: "JSE Top 40 South Africa" },
          { id: "egx30", location: [30.04, 31.24], label: "EGX 30 Egypt" },
          { id: "tadawul", location: [24.71, 46.67], label: "Tadawul Saudi Arabia" },
          { id: "dfm-general", location: [25.20, 55.27], label: "Dubai Financial Market" },
          { id: "adx-general", location: [24.45, 54.38], label: "Abu Dhabi Securities Exchange" },
          { id: "qatar-index", location: [25.29, 51.53], label: "Qatar Stock Exchange Index" },
          { id: "tel-aviv-35", location: [32.08, 34.78], label: "TA-35 Israel" },
          { id: "bist-100", location: [41.01, 28.98], label: "BIST 100 Turkey" },

          { id: "psei", location: [14.60, 120.98], label: "PSEi Philippines" },
          { id: "pakistan-kse100", location: [24.86, 67.01], label: "KSE-100 Pakistan" },
          { id: "colcap", location: [4.71, -74.07], label: "COLCAP Colombia" },
          { id: "peru-general", location: [-12.05, -77.04], label: "S&P/BVL Peru" },

          { id: "vix", location: [41.88, -87.63], label: "CBOE Volatility Index (VIX)" },
          { id: "msci-world", location: [51.51, -0.09], label: "MSCI World Index" },
          { id: "msci-em", location: [51.51, -0.09], label: "MSCI Emerging Markets Index" }
        ]}
      />

      <DataGrid
        title="Stock Index API: Deconstruct the Benchmark"
        subtitle="Go beyond the index value. Analyze daily and historical market index data, constituent weights, sector rotations, and performance attribution for 50,000+ global indices and benchmarks."
        bg={databg}
        stats={[
          { label: 'Indices Tracked', value: '50,000+' },
          { label: 'Daily Constituent Updates', value: '100%' },
          { label: 'Calculation Frequencies', value: 'Daily & Hourly' },
        ]}
      />
      <CardList
        title="Analyze What Moves the Market"
        vid={'https://cdn.studio14a.com/axion/products/cards/indices.mp4'}
        points={[
          {
            "title": "Full Constituent & Weight History",
            "details": "Access the complete historical record of index membership, weightings, and corporate actions. Perfect for accurate backtesting, benchmarking, and understanding index data-driven flows.",
            "cta": "Explore index constituents",
            "url": "/docs"
          },
          {
            "title": "Performance & Attribution Analytics",
            "details": "Decompose index returns by sector, country, factor, and single-stock contribution. Identify what's truly driving benchmark performance versus your portfolio.",
            "cta": "View attribution tools",
            "url": "/products/stocks"
          },
          {
            "title": "Custom Index & Basket Creation",
            "details": "Build, backtest, and monitor custom baskets or thematic indices using our universal securities master. Model bespoke benchmarks with the same rigor as major providers.",
            "cta": "Build a custom index",
            "url": "/docs"
          }
        ]}
      />


      <Responses
        heading='Global market index coverage'
        subheading='Structured index reference and historical pricing data across major exchanges, benchmarks, and sectors'

        ticker='SPX'
        tabs={[
          {
            title: 'Index Tickers',
            description: 'Comprehensive list of all available market indices, including major global benchmarks, sector indices, and thematic indices',
            data: [
              {
                "id": 75,
                "ticker": "AMZIVWAP",
                "name": "Alerian MLP Infrastructure Inde",
                "exchange": "NYS"
              },
              {
                "id": 79,
                "ticker": "AOR-IV",
                "name": "ISHARES CORE 60/40 BALANCED ALL",
                "exchange": "ASE"
              },
              {
                "id": 5,
                "ticker": "AXJO",
                "name": "S&P/ASX 200",
                "exchange": "ASX"
              },
              {
                "id": 6,
                "ticker": "ATX",
                "name": "ATX",
                "exchange": "VIE"
              },
              {
                "id": 8,
                "ticker": "ERAEMEUT",
                "name": "Enhanced RAFI Emerging Markets",
                "exchange": "SNP"
              },
            ]
          },
          {
            title: 'Ticker Details',
            description: 'Detailed metadata for a specific index, including its full name, ticker symbol, and exchange classification',
            data: {
              "id": 43,
              "ticker": "SPX",
              "name": "S&P 500 INDEX",
              "exchange": "WCB",
              "country": "america",
              "timezone": "EDT",
              "lastClose": 7381.27978516,
              "changePct": -1.22457948666668
            },
          },
          {
            title: 'Historical Prices',
            description: 'OHLCV data with support for multiple time frame aggregations (daily, weekly, monthly, quarterly, yearly) and date range filtering',
            data: [
              {
                "time": "2026-06-23T00:00:00.000Z",
                "ticker": "SPX",
                "open": "7366.50976563",
                "high": "7424.16992188",
                "low": "7347.60009766",
                "close": "7381.27978516",
                "volume": "1823022753"
              },
              {
                "time": "2026-06-22T00:00:00.000Z",
                "ticker": "SPX",
                "open": "7500.43994141",
                "high": "7530.00976563",
                "low": "7460.00976563",
                "close": "7472.79003906",
                "volume": "5976740000"
              },
              {
                "time": "2026-06-18T00:00:00.000Z",
                "ticker": "SPX",
                "open": "7487.35986328",
                "high": "7511.06982422",
                "low": "7468.31982422",
                "close": "7500.58007813",
                "volume": "9061110000"
              },
              {
                "time": "2026-06-17T00:00:00.000Z",
                "ticker": "SPX",
                "open": "7524.50000000",
                "high": "7532.16992188",
                "low": "7402.60986328",
                "close": "7420.10009766",
                "volume": "5883740000"
              },
            ]
          },
          {
            title: 'Latest Quote',
            description: 'Latest quote for an index ticker including latest price, EMAs, 52-week range, and YTD performance',
            data: {
              "latestClose": 7381.27978516,
              "latestOpen": 7366.50976563,
              "latestHigh": 7424.16992188,
              "latestLow": 7347.60009766,
              "previousClose": 7472.79003906,
              "week52Range": {
                "high": 7620.8999,
                "low": 6059.25
              },
              "week52AvgClose": 6795.2594,
              "ema30": 7407.2498,
              "ema60": 7273.8562,
              "ema90": 7170.2969,
              "ema120": 7086.324,
              "ytdClosePctChange": 7.6228,
              "volume": 1823022753,
              "avgVolumeLifetime": 983853819.8
            },
          },
          {
            title: 'Market Gainers',
            description: 'Top index gainers ranked by percentage change over the last 5 days',
            data: [
              {
                "ticker": "OMXNLCSEKGI",
                "pctchange": 173.099960774171
              },
              {
                "ticker": "OMXNMCEURGI",
                "pctchange": 167.064157937863
              },
              {
                "ticker": "CTXUSD",
                "pctchange": 151.821900688467
              },
              {
                "ticker": "GSUL",
                "pctchange": 127.726644256383
              },
              {
                "ticker": "CECEEUR",
                "pctchange": 120.538046776129
              },
            ]
          },
          {
            title: 'Market Losers',
            description: 'Top index losers ranked by percentage change over the last 5 days',
            data: [
              {
                "ticker": "MNXR",
                "pctchange": -94.6565385587364
              },
              {
                "ticker": "CEXR",
                "pctchange": -83.9231988849934
              },
              {
                "ticker": "PTIN",
                "pctchange": -69.2595333999004
              },
              {
                "ticker": "VIX1D",
                "pctchange": -23.744773643012
              },
              {
                "ticker": "NKVI.OS",
                "pctchange": -15.3823998207742
              }
            ]
          },
          {
            title: 'Index Components',
            description: 'Constituent holdings for a specific index including ticker symbols and weightings',
            data: {
              symbol: "SPX",
              components: [
                { ticker: "AAPL", name: "Apple Inc.", weight: 7.12 },
                { ticker: "MSFT", name: "Microsoft Corp.", weight: 6.45 },
                { ticker: "AMZN", name: "Amazon.com Inc.", weight: 3.89 },
                { ticker: "NVDA", name: "NVIDIA Corp.", weight: 3.21 },
                { ticker: "GOOGL", name: "Alphabet Inc.", weight: 2.98 },
                { ticker: "META", name: "Meta Platforms Inc.", weight: 2.45 },
                { ticker: "BRKB", name: "Berkshire Hathaway Inc.", weight: 1.78 },
                { ticker: "JPM", name: "JPMorgan Chase & Co.", weight: 1.56 },
                { ticker: "LLY", name: "Eli Lilly & Co.", weight: 1.42 },
                { ticker: "V", name: "Visa Inc.", weight: 1.38 },
              ],
            },
          },
          {
            title: 'Exposure Analysis',
            description: 'Indices that include a given ticker as a constituent component',
            data: [
              {
                symbol: "NDX",
                components: ["AAPL", "MSFT", "AMZN", "NVDA", "GOOGL", "META", "TSLA", "AVGO", "COST", "NFLX"],
              },
              {
                symbol: "SPX",
                components: ["AAPL", "MSFT", "AMZN", "NVDA", "GOOGL", "META", "BRKB", "JPM", "LLY", "V"],
              },
              {
                symbol: "DJI",
                components: ["AAPL", "MSFT", "AMZN", "JPM", "V", "CSCO", "INTC", "IBM", "DIS", "HD"],
              },
            ],
          },
        ]}
      />
      <DatasetDetails full={true}
        title="Comprehensive Index Coverage"
        subtitle="Track major benchmark indices, sector indices, and custom factor-based indices with complete transparency into constituent lists, weightings, methodology documents, and rebalancing schedules. Build, backtest, and monitor custom baskets with full constituent and weight history."
        cta="Get API Access"
        ctaLink="/login"
        tiles={[
          {
            img: '3.webp',
            title: 'We track over 50,000 indices',
            caption: 'You can monitor benchmark, sector, and custom indices across every global market.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '14.webp',
            title: 'We cover 8+ asset classes',
            caption: 'You can access indices spanning equities, fixed income, commodities, and more.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '0.webp',
            title: 'We provide 40+ years of history',
            caption: 'You can backtest with full constituent and weight history for robust index analysis.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '1.webp',
            title: 'We update constituents daily',
            caption: 'You always have the latest index compositions with full corporate action adjustments.',
            cn: 'overthetop',
            tile: <W4/>
          },
          {
            img: '6.webp',
            title: 'We cover every rebalancing event',
            caption: 'You get complete rebalancing schedules, methodology documents, and full transparency.',
            cn: 'scaled corner',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Stock index data that powers your workflow."
        leftCell={{ stat: '50,000+', title: 'Indices Tracked.', desc: 'Benchmarks spanning equities, fixed income, commodities, and custom sectors, updated daily.', showChart: true }}
        rightCells={[
          { stat: '40+ yr', title: 'Historical Depth.', desc: 'Full constituent and weight history for robust backtesting and attribution.' },
          { stat: '100%', title: 'Daily Updates.', desc: 'Constituent changes, rebalancing events, and corporate actions refreshed every day.', boldDesc: true },
        ]}
      /><ProductFAQ slug="indices" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default StocksPage;

